import classnames from "classnames";
import React, { Fragment, useEffect, useState } from "react";

import { DeliveryTypes } from "@utils/enums";
import { debounce } from "@utils/general";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Input } from "@components/General/Form/input.component";
import { InputGroup } from "@components/General/Form/inputGroup.component";
import { InputGroupAddon } from "@components/General/Form/inputGroupAddon.component";

type Props = {
    handleChange: (newQuantity: number, oldQuantity: number) => void;
    value: number;
    roundingValue: number | undefined;
    minimumOrderQuantity: number;
    maximumOrderQuantity: number;
    deliveryType?: DeliveryTypes;
    isFree?: boolean;
    isFreeEntryAddedByCustomer?: boolean;
    isUpdatingCart: boolean;
    hasProductMultipleCartEntries?: boolean;
};

const debounceChange = debounce(
    (newQuantity: number, oldQuantity: number, handleChange: Function) => {
        handleChange(newQuantity, oldQuantity);
    },
    500,
);

const AmountInput = ({
    handleChange,
    value,
    roundingValue,
    minimumOrderQuantity,
    maximumOrderQuantity,
    deliveryType,
    isFree,
    isFreeEntryAddedByCustomer,
    isUpdatingCart,
    hasProductMultipleCartEntries,
}: Props) => {
    const { t } = useGranite();

    const defaultRoundingValue: number = 1;
    const [quantity, setQuantity] = useState(0);

    useEffect(() => {
        setQuantity(value || 0);
    }, []);

    useEffect(() => {
        if (!isUpdatingCart) {
            setQuantity(value || 0);
        }
    }, [isUpdatingCart]);

    useEffect(() => {
        if (!isUpdatingCart && value !== quantity) {
            setQuantity(value || 0);
        }
    }, [value]);

    const determineRoundingValue = () => {
        if (deliveryType !== DeliveryTypes.ClickAndCollect) {
            return roundingValue ? roundingValue : 0;
        } else {
            return defaultRoundingValue;
        }
    };

    const onDecrease = () => {
        const newQuantity = quantity - determineRoundingValue();

        if (isUpdatingCart || newQuantity < minimumOrderQuantity) {
            return;
        }

        setQuantity(newQuantity);
        debounceChange(newQuantity, value, handleChange);
    };

    const onIncrease = () => {
        const newQuantity = quantity + determineRoundingValue();

        if (isUpdatingCart || newQuantity > maximumOrderQuantity) {
            return;
        }

        setQuantity(newQuantity);
        debounceChange(newQuantity, value, handleChange);
    };

    const onInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const newQuantity = parseInt(e.target.value);
        setQuantity(newQuantity);

        if (
            isUpdatingCart ||
            isNaN(newQuantity) ||
            newQuantity < minimumOrderQuantity ||
            newQuantity > maximumOrderQuantity
        ) {
            return;
        }

        debounceChange(newQuantity, value, handleChange);
    };

    const onInputBlur = () => {
        if (quantity < minimumOrderQuantity || isNaN(quantity)) {
            setQuantity(minimumOrderQuantity);
            debounceChange(minimumOrderQuantity, value, handleChange);
        }

        if (quantity > maximumOrderQuantity) {
            setQuantity(maximumOrderQuantity);
            debounceChange(maximumOrderQuantity, value, handleChange);
        }
    };

    const decreaseIsDisabled = () => {
        return (
            quantity - determineRoundingValue() < minimumOrderQuantity ||
            isUpdatingCart
        );
    };

    const increaseIsDisabled = () => {
        return (
            quantity + determineRoundingValue() > maximumOrderQuantity ||
            isUpdatingCart
        );
    };

    const isQuantityEditable =
        !isFree ||
        (isFreeEntryAddedByCustomer && !hasProductMultipleCartEntries);

    return (
        <Fragment>
            <InputGroup
                className="delivery-amount__inputgroup"
                data-test="deliveryAmount-inputGroup"
            >
                {isQuantityEditable && (
                    <InputGroupAddon
                        data-test="deliveryAmount-inputGroup-button-decrease"
                        className="delivery-amount__inputgroup-addon"
                        addonType="prepend"
                        onClick={() => onDecrease()}
                    >
                        <span
                            className={classnames(
                                decreaseIsDisabled()
                                    ? "delivery-amount--disabled"
                                    : "",
                                "input-group-text",
                            )}
                        >
                            <i className="las la-minus" />
                        </span>
                    </InputGroupAddon>
                )}
                <Input
                    data-test="deliveryAmount-inputGroup-inputField"
                    disabled={!isQuantityEditable || isUpdatingCart}
                    min={minimumOrderQuantity}
                    value={quantity}
                    onChange={e => onInputChange(e)}
                    onBlur={() => onInputBlur()}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    aria-label={t("accessibility.ecommerce.amount.input.label")}
                />
                {isQuantityEditable && (
                    <InputGroupAddon
                        data-test="deliveryAmount-inputGroup-button-increase"
                        className="delivery-amount__inputgroup-addon"
                        addonType="append"
                        onClick={() => onIncrease()}
                    >
                        <span
                            className={classnames(
                                increaseIsDisabled()
                                    ? "delivery-amount--disabled"
                                    : "",
                                "input-group-text",
                            )}
                        >
                            <i className="las la-plus" />
                        </span>
                    </InputGroupAddon>
                )}
            </InputGroup>
        </Fragment>
    );
};

export default AmountInput;
